import 'pleach-ds/dist/pleach.css';
import './app.css';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { GATE_CODES, isApiError } from '@/api/client';
import { routeForGateCode, sessionKey } from '@/lib/session';
import { ToastProvider } from '@/lib/toast';
import { routeTree } from './routeTree.gen';

// Any API call that trips a gate (signed out, terms, password change...)
// sends the chef to the page that clears it, wherever it happened.
function onGateError(err: unknown) {
  // A section closed under this tab (an admin moved its stage, or a level
  // changed): the screen shows Coming soon from the 403, and the session
  // catches up so the rail and the other gates follow (docs/ACCESS.md).
  // Only when the session still says the section is open: a screen that
  // expects the 403 (the ingredient form asks for the allergen list either
  // way) must not refetch the session on every visit and window focus.
  if (isApiError(err, 'feature_locked')) {
    const key = typeof err.body.feature === 'string' ? err.body.feature : null;
    const open = (queryClient.getQueryData(sessionKey) as { unlocked_features?: string[] } | undefined)
      ?.unlocked_features;
    if (!key || open?.includes(key)) void queryClient.invalidateQueries({ queryKey: sessionKey });
    return;
  }
  if (!isApiError(err) || !GATE_CODES.has(err.code)) return;
  if (err.code === 'csrf_failed') return; // the screen shows the message; a reload fixes it
  // Was this browser signed in a moment ago? Then the server ended the
  // session (password changed elsewhere, account closed, admin action):
  // tell the chef why instead of silently showing the sign-in form.
  const wasSignedIn = Boolean(
    (queryClient.getQueryData(sessionKey) as { authenticated?: boolean } | undefined)?.authenticated,
  );
  void queryClient.invalidateQueries({ queryKey: sessionKey });
  const to = routeForGateCode(err.code);
  if (to && window.location.pathname !== to) {
    if (to === '/login') {
      const reason =
        err.code === 'account_deactivated' ? 'account_closed' : wasSignedIn ? 'signed_out' : undefined;
      void router.navigate({ to: '/login', search: { next: window.location.pathname, reason } });
    } else {
      void router.navigate({ to });
    }
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onGateError }),
  mutationCache: new MutationCache({ onError: onGateError }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, err) => !isApiError(err) && count < 2,
    },
    mutations: { retry: false },
  },
});

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
